import { useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { useDispatch } from "react-redux";
import {
  Box, CircularProgress, Divider, Grid, Stack, Tooltip, Typography, styled,
} from "@mui/material";
import CustomBackButton from "@/components/custom/custom-back-button";
import { CustomButton } from "@/components/custom/custom-button";
import { CustomErrorMessage } from "@/components/custom/custom-error-message";
import CustomConfirmDialog from "@/components/custom/custom-confirm-dialog";
import CustomStatusChip from "@/components/custom/custom-status-chip";
import { DataTable } from "@/components/custom/data-table";
import {
  useAccountClosure, useApproveClosure, useRejectClosure,
  useSupportTicketReopen, useClosureAudit,
} from "@/hooks/use-account-closures";
import { showToast } from "@/redux/actions/toast-actions";
import { formatCurrency } from "@/utils/format-currency";
import { formatLabel } from "@/utils/format-label";
import { closureRejectSchema, supportReopenSchema } from "@/schemas/closure.schema";
import PendingActionsPanel from "./_components/pending-actions-panel";
import { getAuditColumns } from "./_components/closure-audit-columns";
import { CLOSURE_STATUS_STYLES, ACCOUNT_STATE_STYLES } from "../data";
import CustomReasonDialog from "@/components/custom/custom-reason-dialog";

const SectionRoot = styled("section")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(2),
  margin: theme.spacing(3),
}));

const Card = styled(Box)(({ theme }) => ({
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: 12,
  padding: theme.spacing(2.5),
  backgroundColor: theme.palette.background.paper,
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(2),
}));

const SectionLabel = styled(Typography)(({ theme }) => ({
  fontSize: "0.75rem",
  fontWeight: 600,
  textTransform: "uppercase",
  letterSpacing: "0.05em",
  color: theme.palette.text.secondary,
}));

const Field = ({ label, value, span = 3 }) => (
  <Grid item xs={12} sm={6} md={span}>
    <Typography variant="caption" color="text.secondary" display="block">{label}</Typography>
    <Typography fontSize="0.875rem" fontWeight={500} mt={0.25} sx={{ wordBreak: "break-word" }}>
      {value ?? "N/A"}
    </Typography>
  </Grid>
);

const AccountClosureDetail = () => {
  const { id } = useParams();
  const dispatch = useDispatch();

  const [approveOpen, setApproveOpen] = useState(false);
  const [rejectOpen, setRejectOpen] = useState(false);
  const [reopenOpen, setReopenOpen] = useState(false);

  const { data: request, isLoading, isError } = useAccountClosure(id);

  const approveMutation = useApproveClosure();
  const rejectMutation = useRejectClosure();
  const reopenMutation = useSupportTicketReopen();

  const { data: audit, isLoading: auditLoading } = useClosureAudit({
    accountUserId: request?.accountUserId,
  });

  const auditColumns = useMemo(() => getAuditColumns(), []);

  if (isLoading) {
    return (
      <SectionRoot>
        <Stack direction="row" alignItems="center" spacing={2}>
          <CircularProgress size={16} thickness={4} />
          <Typography variant="body2" color="text.secondary">
            Loading closure request...
          </Typography>
        </Stack>
      </SectionRoot>
    );
  }

  if (isError || !request) {
    return (
      <SectionRoot>
        <Stack direction="row" alignItems="center" spacing={2}>
          <CustomBackButton />
          <Typography variant="h6" fontWeight={500}>Closure Request</Typography>
        </Stack>
        <CustomErrorMessage message="This closure request could not be loaded." />
      </SectionRoot>
    );
  }

  const pendingActions = request.pendingActions ?? [];
  const blockingActions = pendingActions.filter((a) => a.blocking);
  const isPendingReview = request.status === "PENDING_REVIEW";
  const isArchived = request.accountState === "ARCHIVED";
  const isDeciding = approveMutation.isPending || rejectMutation.isPending;

  // Client-side disable is a courtesy only — the approve endpoint re-checks and
  // refuses with 409 regardless of what is rendered here.
  const approveBlocked = request.blocked || blockingActions.length > 0;
  const blockerSummary = blockingActions
    .map((a) => formatLabel(a.type))
    .join(", ");

  // 409 covers two different situations: still blocked, or already decided by
  // another admin (standing decision arrives in `data`). Both resolve the same
  // way for the user — the panel/status has already been refetched by onSettled.
  const handleDecisionError = (err, fallback) => {
    if (err?.statusCode === 409) {
      const standing = err?.data?.status;
      dispatch(showToast(
        standing
          ? `This request was already ${formatLabel(standing).toLowerCase()} by another admin.`
          : err?.message ?? "This request can no longer be approved — open items remain.",
        "error",
      ));
      return;
    }
    dispatch(showToast(err?.message ?? fallback, "error"));
  };

  return (
    <SectionRoot>
      <Stack direction="row" alignItems="center" spacing={2} flexWrap="wrap" useFlexGap>
        <CustomBackButton />
        <Typography variant="h6" fontWeight={500}>
          {request.accountOwnerName ?? "Closure Request"}
        </Typography>
        <CustomStatusChip status={request.status} styles={CLOSURE_STATUS_STYLES} />
        <CustomStatusChip status={request.accountState} styles={ACCOUNT_STATE_STYLES} />

        <Box sx={{ flex: 1 }} />

        <Stack direction="row" spacing={1}>
          {isPendingReview && (
            <Tooltip
              title={approveBlocked ? `Blocked by: ${blockerSummary || "open items on this account"}` : ""}
              arrow placement="top"
            >
              <span>
                <CustomButton
                  variantType="outline"
                  sx={{ height: 35 }}
                  disabled={approveBlocked || isDeciding}
                  onClick={() => setApproveOpen(true)}
                >
                  Approve
                </CustomButton>
              </span>
            </Tooltip>
          )}

          {isPendingReview && (
            <CustomButton
              variantType="destructive"
              sx={{ height: 35 }}
              disabled={isDeciding}
              onClick={() => setRejectOpen(true)}
            >
              Reject
            </CustomButton>
          )}

          {isArchived && (
            <CustomButton
              sx={{ height: 35 }}
              disabled={reopenMutation.isPending}
              onClick={() => setReopenOpen(true)}
            >
              Reopen via Support Ticket
            </CustomButton>
          )}
        </Stack>
      </Stack>

      <Card>
        <SectionLabel>Request</SectionLabel>
        <Grid container spacing={3}>
          <Field label="Submitted" value={request.submittedAt} />
          <Field label="Platform" value={formatLabel(request.platform)} />
          <Field label="Reason Category" value={formatLabel(request.reasonCategory)} />
          <Field label="Request ID" value={request.id} />
          <Field label="Additional Detail" value={request.reasonDetails} span={12} />
        </Grid>

        {(request.decidedAt || request.decisionReason) && (
          <>
            <Divider />
            <SectionLabel>Decision</SectionLabel>
            <Grid container spacing={3}>
              <Field label="Decided By" value={request.decidedByName ?? request.decidedBy} />
              <Field label="Decided At" value={request.decidedAt} />
              <Field label="Reason" value={request.decisionReason} span={6} />
            </Grid>
          </>
        )}
      </Card>

      <Card>
        <SectionLabel>Account</SectionLabel>
        <Grid container spacing={3}>
          <Field label="Owner" value={request.accountOwnerName} />
          <Field label="Email" value={request.accountOwnerEmail} />
          <Field label="Phone" value={request.accountOwnerPhone} />
          <Field label="User ID" value={request.accountUserId} />
          <Field label="Balance" value={request.balance != null ? formatCurrency(request.balance) : "N/A"} />
          <Field label="KYC Status" value={formatLabel(request.kycStatus)} />
          <Field label="Account State" value={formatLabel(request.accountState)} />
          <Field label="Merchant ID" value={request.merchantId} />
        </Grid>
      </Card>

      <Card>
        <SectionLabel>Pending Actions</SectionLabel>
        <PendingActionsPanel actions={pendingActions} />
      </Card>

      <Card>
        <SectionLabel>Decision Audit</SectionLabel>
        <DataTable
          data={audit ?? []}
          columns={auditColumns}
          isLoading={auditLoading}
          noDataMessage="No decisions recorded for this account yet."
        />
      </Card>

      <CustomConfirmDialog
        open={approveOpen}
        onClose={() => setApproveOpen(false)}
        title="Approve account closure"
        description="The account is deactivated immediately, active sessions are terminated, and the 60-day reversal window starts now. This cannot be undone from here."
        confirmLabel="Approve closure"
        variant="danger"
        mutation={approveMutation}
        onConfirm={() =>
          approveMutation.mutate(
            { id },
            {
              onSuccess: (res) => {
                setApproveOpen(false);
                dispatch(showToast(res?.message ?? "Closure request approved", "success"));
              },
              onError: (err) => {
                setApproveOpen(false);
                handleDecisionError(err, "Could not approve this request");
              },
            },
          )
        }
      />

      <CustomReasonDialog
        open={rejectOpen}
        onClose={() => setRejectOpen(false)}
        title="Reject account closure"
        description="The account stays active. This reason is shown to the merchant and written to the audit log."
        label="Reason for rejection"
        placeholder="Explain why this request is being rejected (minimum 10 characters)..."
        confirmLabel="Reject request"
        pendingLabel="Rejecting..."
        variant="danger"
        schema={closureRejectSchema}
        mutation={rejectMutation}
        onConfirm={(reason) =>
          rejectMutation.mutate(
            { id, reason },
            {
              onSuccess: (res) => {
                setRejectOpen(false);
                dispatch(showToast(res?.message ?? "Closure request rejected", "success"));
              },
              onError: (err) => {
                setRejectOpen(false);
                handleDecisionError(err, "Could not reject this request");
              },
            },
          )
        }
      />

      <CustomReasonDialog
        open={reopenOpen}
        onClose={() => setReopenOpen(false)}
        title="Reopen account via support ticket"
        description="Only available while the account is Archived (day 60–90). Past day 90 no reopen is possible."
        label="Business justification"
        placeholder="Record the justification from the support ticket..."
        confirmLabel="Reopen account"
        pendingLabel="Reopening..."
        variant="warning"
        schema={supportReopenSchema}
        mutation={reopenMutation}
        onConfirm={(reason) =>
          reopenMutation.mutate(
            { userId: request.accountUserId, reason },
            {
              onSuccess: (res) => {
                setReopenOpen(false);
                dispatch(showToast(res?.message ?? "Account reopened", "success"));
              },
              onError: (err) => {
                setReopenOpen(false);
                dispatch(showToast(err?.message ?? "Could not reopen this account", "error"));
              },
            },
          )
        }
      />
    </SectionRoot>
  );
};

export default AccountClosureDetail;